import { Controller } from "@hotwired/stimulus"

// Toast container. Listens for the `toast` custom event on document and
// renders a transient notification.
//
// Usage from anywhere:
//   document.dispatchEvent(new CustomEvent("toast", {
//     detail: { message: "Salvo!", variant: "success", duration: 4000 }
//   }))
//
// Variants drive both the icon and the left border color.
//
// A11y: o timer pausa em hover/focus (WAI-ARIA best practice — usuários com
// dificuldade motora ou visão baixa precisam de tempo extra pra ler).
const VARIANT_BORDERS = {
  default:     "border-border",
  success:     "border-green-500/60",
  warning:     "border-yellow-500/60",
  destructive: "border-destructive/60"
}

export default class extends Controller {
  static targets = [
    "template",
    "iconDefault", "iconSuccess", "iconWarning", "iconDestructive"
  ]

  show(e) {
    const { message, variant = "default", duration = 4000 } = e.detail || {}
    if (!message) return

    const node = this.templateTarget.content.firstElementChild.cloneNode(true)
    node.querySelector('[data-toast-target="message"]').textContent = message
    node.classList.add(VARIANT_BORDERS[variant] || VARIANT_BORDERS.default, "border-l-4")

    const iconHost = node.querySelector('[data-toast-target="icon"]')
    const iconKey  = `icon${variant.charAt(0).toUpperCase() + variant.slice(1)}Target`
    const iconTpl  = this[iconKey] || this.iconDefaultTarget
    iconHost.appendChild(iconTpl.content.cloneNode(true))

    this.element.appendChild(node)

    // Animate in next frame so the transition fires.
    requestAnimationFrame(() => {
      node.classList.remove("opacity-0", "-translate-y-2", "sm:translate-x-2")
    })

    // Auto-dismiss com pausa em hover/focus.
    let remaining = duration
    let startedAt = Date.now()
    let timerId   = setTimeout(() => this._remove(node), remaining)

    const pause = () => {
      clearTimeout(timerId)
      remaining -= Date.now() - startedAt
    }
    const resume = () => {
      startedAt = Date.now()
      timerId   = setTimeout(() => this._remove(node), Math.max(remaining, 500))
    }

    node.addEventListener("mouseenter", pause)
    node.addEventListener("focusin",    pause)
    node.addEventListener("mouseleave", resume)
    node.addEventListener("focusout",   resume)
  }

  dismiss(e) {
    this._remove(e.currentTarget.closest('[data-toast-target="item"]'))
  }

  _remove(node) {
    if (!node) return
    node.classList.add("opacity-0", "-translate-y-2", "sm:translate-x-2")
    setTimeout(() => node.remove(), 200)
  }
}
